import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, ArrowUpRight } from "lucide-react";
import { ARTISTS, SOCIALS } from "../../data/velvet";
import { Overline } from "./Reveal";

const EASE = [0.16, 1, 0.3, 1];

const ArtistCard = ({ artist, index, onOpen }) => (
  <motion.button
    data-testid={`artist-card-${artist.id}`}
    onClick={() => onOpen(artist)}
    initial={{ opacity: 0, y: 60 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-6%" }}
    transition={{ duration: 1, ease: EASE, delay: (index % 3) * 0.1 }}
    className={`group relative block h-full min-h-[340px] w-full overflow-hidden rounded-sm text-left md:min-h-[420px] ${artist.span}`}
  >
    <img
      src={artist.image}
      alt={`Portrait of ${artist.name}, ${artist.handle}`}
      className="absolute inset-0 h-full w-full object-cover object-top grayscale-[0.5] brightness-[0.72] transition-[filter,transform] duration-700 ease-out group-hover:scale-[1.04] group-hover:grayscale-0 group-hover:brightness-90"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-vt-black via-vt-black/25 to-transparent" />
    <div className="absolute inset-0 border border-vt-gold/0 transition-colors duration-500 group-hover:border-vt-gold/40" />

    <div className="relative flex h-full flex-col justify-between p-6 md:p-7">
      <div className="flex justify-end">
        <span className="flex h-10 w-10 items-center justify-center rounded-full border border-vt-cream/25 text-vt-cream transition-[background-color,color,transform] duration-500 group-hover:rotate-90 group-hover:border-vt-gold group-hover:bg-vt-gold group-hover:text-vt-black">
          <Plus size={16} />
        </span>
      </div>
      <div>
        <Overline className="text-vt-gold/70">{artist.era}</Overline>
        <h3 className="mt-2 font-serif text-3xl font-medium leading-none tracking-tight text-vt-cream md:text-4xl">
          {artist.name}
        </h3>
        <p className="mt-1 font-body text-sm font-light italic tracking-wide text-vt-ash">
          {artist.handle}
        </p>
      </div>
    </div>
  </motion.button>
);

const StreamLinks = () => (
  <div className="flex flex-wrap gap-3">
    {[["Spotify", SOCIALS.spotify]].map(([label, href]) => (
      <a
        key={label}
        data-testid={`modal-stream-${label.toLowerCase().replace(" ", "-")}`}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="group inline-flex items-center gap-2 rounded-full border border-vt-gold/40 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-vt-cream transition-colors duration-300 hover:bg-vt-gold hover:text-vt-black"
      >
        {label}
        <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    ))}
  </div>
);

const ArtistModal = ({ artist, onClose }) => (
  <motion.div
    data-testid="artist-modal"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4 }}
    onClick={onClose}
    className="fixed inset-0 z-[120] flex items-stretch justify-end bg-vt-black/80 backdrop-blur-md md:items-center md:justify-center md:p-8"
  >
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ duration: 0.6, ease: EASE }}
      onClick={(e) => e.stopPropagation()}
      className="relative flex h-full w-full max-w-6xl flex-col overflow-y-auto bg-vt-surface md:h-auto md:max-h-[88vh] md:flex-row md:rounded-sm"
    >
      <button
        data-testid="artist-modal-close"
        onClick={onClose}
        className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-vt-cream/20 bg-vt-black/40 text-vt-cream backdrop-blur-md transition-colors duration-300 hover:border-vt-gold hover:text-vt-gold"
        aria-label="Close"
      >
        <X size={20} />
      </button>

      <div className="relative h-[42vh] w-full shrink-0 overflow-hidden md:h-auto md:w-[44%]">
        <img
          src={artist.image}
          alt={`Portrait of ${artist.name}`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-vt-surface via-transparent to-transparent md:bg-gradient-to-r" />
      </div>

      <div className="flex flex-1 flex-col justify-center px-7 py-10 md:px-14 md:py-16">
        <Overline className="text-vt-gold/80">{artist.era}</Overline>
        <h2 className="mt-3 font-serif text-5xl font-light leading-none tracking-tight text-vt-cream md:text-6xl">
          {artist.name}
        </h2>
        <p className="mt-2 font-body text-lg font-light italic tracking-wide text-vt-gold">
          {artist.handle}
        </p>

        <div className="my-7 vt-hairline" />

        <p className="font-body text-base font-light leading-relaxed tracking-wide text-vt-ash">
          {artist.bio}
        </p>
        <p className="mt-4 font-body text-base font-light leading-relaxed tracking-wide text-vt-ash">
          {artist.story}
        </p>

        <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
          <div>
            <Overline>Musical Identity</Overline>
            <p className="mt-3 font-serif text-xl italic leading-snug text-vt-cream">
              {artist.identity}
            </p>
          </div>
          <div>
            <Overline>Featured Releases</Overline>
            <ul className="mt-3 space-y-1.5">
              {artist.tracks.map((t) => (
                <li key={t} className="font-body text-sm font-light tracking-wide text-vt-ash">
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10">
          <Overline className="mb-3 block">Stream</Overline>
          <StreamLinks />
        </div>
      </div>
    </motion.div>
  </motion.div>
);

export const Voices = () => {
  const [active, setActive] = useState(null);

  return (
    <section
      id="voices"
      data-testid="voices-section"
      className="relative mx-auto max-w-[1600px] px-6 py-28 md:px-16 md:py-40 lg:px-24"
    >
      <div className="mb-14 flex flex-col gap-6 md:mb-20 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-6 flex items-center gap-4">
            <Overline>The Voices</Overline>
            <div className="vt-hairline w-24" />
          </div>
          <h2 className="font-serif text-4xl font-light leading-[1.02] tracking-tight text-vt-cream sm:text-5xl md:text-6xl lg:text-7xl">
            Five voices, <span className="italic text-vt-gold">one saga.</span>
          </h2>
        </div>
        <p className="max-w-sm font-body text-base font-light leading-relaxed tracking-wide text-vt-ash">
          The recurring characters of the Testament — appearing and reappearing
          across records like a family bound by sound. Choose a name to enter
          their world.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:auto-rows-[minmax(200px,auto)] md:grid-cols-12 md:gap-5">
        {ARTISTS.map((a, i) => (
          <ArtistCard key={a.id} artist={a} index={i} onOpen={setActive} />
        ))}
      </div>

      <AnimatePresence>
        {active && <ArtistModal artist={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
};
